import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  Fade,
  Zoom,
  IconButton,
  Paper,
  LinearProgress,
  Stack,
  alpha,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Divider
} from '@mui/material';

import civiqLogo from '../img/civiq.jpg';

import {
  Close as CloseIcon,
  AccountBox as ProfileIcon,
  People as DemographicsIcon,
  Timeline as HistoricalIcon,
  TrendingUp as TrendsIcon,
  Article as NewsIcon,
  Group as PartyIcon,
  Assessment as EconomyIcon,
  Psychology as ForecastIcon,
  Compare as CompareIcon,
  ArrowUpward,
  ThumbUp,
  ThumbDown,
  TrendingFlat
} from '@mui/icons-material';

// Import custom hooks and utilities
import { useConstituencyData } from '../hooks/useConstituencyData';
import { formatNumber, formatPercentage } from '../utils/dataUtils';
import { ProfileModal, VotingModal, ResultsModal, NewsModal } from './ModalComponents';

const SENTIMENT_COLORS = { positive: '#4caf50', neutral: '#ff9800', negative: '#f44336' };

const CandidateDashboard = () => {
  const [openModal, setOpenModal] = useState(null);
  const [detailedResults, setDetailingResults] = useState(null);
  const [loadingDetailedResults, setLoadingDetailedResults] = useState(false);

  // Use the custom hook for constituency data
  const {
    loading,
    error,
    constituencies,
    selectedConstituency,
    currentData,
    changeConstituency
  } = useConstituencyData();

  const handleTileClick = (modalType) => {
    setOpenModal(modalType);
  };

  const handleCloseModal = () => {
    setOpenModal(null);
  };

  const getSentimentColor = (sentiment) => {
    return SENTIMENT_COLORS[sentiment.toLowerCase()] || '#757575';
  };

  // Show loading state
  if (loading && !currentData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading candidate data...</Typography>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body1">
          Please check your connection and try refreshing the page.
        </Typography>
      </Box>
    );
  }

  // Tile components for candidate dashboard
  const tiles = [
    {
      id: 'profile',
      title: 'Candidate Profile',
      icon: <ProfileIcon />,
      color: '#2563eb',
      gradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      summary: (
        <Box>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {currentData?.results?.winner?.candidate || 'Candidate Name'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {currentData?.results?.winner?.party || 'Party Name'}
              </Typography>
            </Box>
            
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold">
                    {currentData?.results?.winner?.votes ? formatNumber(currentData.results.winner.votes) : 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Votes Received
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold">
                    {currentData?.results?.winner?.vote_percentage?.toFixed(1) || 'N/A'}%
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Vote Share
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold">
                    {currentData?.results?.margin?.votes ? formatNumber(currentData.results.margin.votes) : 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Victory Margin
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Stack>
        </Box>
      )
    },
    {
      id: 'voting',
      title: 'Election History',
      icon: <HistoricalIcon />,
      color: '#dc2626',
      gradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      summary: (
        <Box>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight="bold">Past Performance</Typography>
            <Box>
              <Typography variant="body2">Previous Elections:</Typography>
              <Box sx={{ mt: 1 }}>
                {['2020', '2015', '2010'].map((year) => (
                  <Box key={year} sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{year}:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {Math.floor(Math.random() * 30 + 40)}% votes
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Stack>
        </Box>
      )
    },
    {
      id: 'performance',
      title: 'Performance Metrics',
      icon: <TrendsIcon />,
      color: '#7c3aed',
      gradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      summary: (
        <Box>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight="bold">Key Metrics</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2">Attendance:</Typography>
                <Typography variant="h6" fontWeight="bold">78%</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">Questions:</Typography>
                <Typography variant="h6" fontWeight="bold">245</Typography>
              </Grid>
            </Grid>
          </Stack>
        </Box>
      )
    },
    {
      id: 'news',
      title: 'Media Coverage',
      icon: <NewsIcon />,
      color: '#f97316',
      gradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      summary: (
        <Box>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight="bold">Recent Coverage</Typography>
            <Box>
              {[
                { title: 'Campaign Update', sentiment: 'Positive' },
                { title: 'Policy Statement', sentiment: 'Neutral' }
              ].map((news, index) => (
                <Box key={index} sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ color: getSentimentColor(news.sentiment) }}>
                    {news.title}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Stack>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
      {/* Header Section */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={civiqLogo}
              alt="CivIQ Logo"
              sx={{
                width: 60,
                height: 60,
                borderRadius: 2,
                border: '2px solid #1976d2',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
              }}
            />
            <Box>
              <Typography 
                variant="h4" 
                fontWeight="bold" 
                color="primary.main"
                gutterBottom
              >
                CivIQ Candidate Dashboard
              </Typography>
              <Typography variant="h6" color="text.secondary" fontWeight={500}>
                Detailed Candidate Analysis & Performance Metrics
              </Typography>
            </Box>
          </Box>
          
          <Box>
            <Autocomplete
              options={constituencies}
              getOptionLabel={(option) => option.fullName}
              value={constituencies.find(c => c.code === selectedConstituency) || null}
              onChange={(event, newValue) => {
                if (newValue) {
                  changeConstituency(newValue.code);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Constituency"
                  variant="outlined"
                  sx={{ minWidth: 300 }}
                />
              )}
              loading={loading}
            />
          </Box>
        </Box>
      </Paper>

      {/* Main Dashboard Layout */}
      <Grid container spacing={4}>
        {/* Main Tiles */}
        <Grid item xs={12} lg={12}>
          <Grid container spacing={2}>
            {tiles.map((tile) => (
              <Grid item xs={12} sm={6} md={3} key={tile.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    height: '240px',
                    background: tile.gradient,
                    color: tile.color,
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 2,
                    border: `2px solid ${tile.color}`,
                    boxShadow: `0 4px 12px ${alpha(tile.color, 0.15)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 20px ${alpha(tile.color, 0.25)}`,
                    },
                  }}
                  onClick={() => handleTileClick(tile.id)}
                >
                  <CardHeader
                    avatar={
                      <Avatar sx={{ bgcolor: tile.color }}>
                        {tile.icon}
                      </Avatar>
                    }
                    title={tile.title}
                  />
                  <CardContent>
                    {tile.summary}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      {/* Modal */}
      <Dialog
        open={!!openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{tiles.find(t => t.id === openModal)?.title}</Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* Modal content will be similar to ConstituencyDashboard but focused on candidate data */}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CandidateDashboard;
